import React, { useEffect, useState } from 'react';
import UnitSelect from '../Common/UnitSelect';
import QuantityConversionHint from '../Common/QuantityConversionHint';
import { getUnitFormulaForProduct } from '../../utils/unitSelectUtils';

const KARDEX_ES = {
  saldo_inicial: 'Saldo inicial',
  traspaso: 'Traspaso',
  recepcion: 'Recepción',
  venta: 'Venta',
  compra: 'Compra',
  devolucion: 'Devolución',
  descuento_concedido: 'Desc. concedido',
  descuento_con_mercaderia: 'Desc. c/ mercadería',
  descuento_obtenido: 'Desc. obtenido',
  transferencia_gratuita: 'Transf. gratuita',
  consumo: 'Consumo',
  salida_produccion: 'Salida a producción',
  entrada_produccion: 'Entrada de producción',
  entrada_producto_terminado: 'Entrada producto terminado',
  reversa_traspaso: 'Reversa traspaso',
  dar_por_perdido: 'Dar por perdido',
};

const CONDITION_ES = {
  good: 'Bueno',
  damaged: 'Dañado',
  defective: 'Defectuoso',
  reusable: 'Reutilizable',
  non_reusable: 'No reutilizable',
};

function labelKardex(code) {
  if (!code) return '—';
  return KARDEX_ES[code] || code;
}

function labelCondition(c) {
  if (!c) return '—';
  return CONDITION_ES[c] || c;
}

/**
 * Vista previa y validación antes de volcar filas importadas al formulario de la guía.
 */
const GuideItemsImportPreviewModal = ({
  isOpen,
  onClose,
  onConfirm,
  fileName = '',
  items = [],
  errors = [],
  warnings = [],
  isExit = false,
  projects = [],
  products = [],
  units = [],
}) => {
  const [rows, setRows] = useState([]);

  useEffect(() => {
    if (isOpen) {
      setRows(items.map((item) => ({ ...item })));
    }
  }, [isOpen, items]);

  if (!isOpen) return null;

  const nOk = rows.length;
  const nErr = errors.length;
  const nWarn = warnings.length;

  const projectLabel = (id) => {
    if (id == null || id === '') return '—';
    const p = projects.find((x) => String(x.id) === String(id));
    if (p) {
      return [p.code, p.name].filter(Boolean).join(' — ') || String(id);
    }
    return `ID ${id}`;
  };

  const canAdd = nOk > 0;
  const canEditUnits = products.length > 0 && units.length > 0;

  const findProduct = (row) =>
    products.find((p) => String(p.id) === String(row.product_id)) || null;

  const handleUnitChange = (index, unitCode) => {
    setRows((prev) => {
      const next = [...prev];
      const product = findProduct(next[index]);
      next[index] = {
        ...next[index],
        unit_of_measure: unitCode,
        _unitFormula: product ? getUnitFormulaForProduct(product, unitCode) : '',
      };
      return next;
    });
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="guide-import-preview-title"
    >
      <div className="bg-white rounded-xl shadow-xl max-w-5xl w-full max-h-[90vh] flex flex-col">
        <div className="p-4 sm:p-5 border-b border-gray-200 flex justify-between items-start gap-3">
          <div>
            <h2 id="guide-import-preview-title" className="text-lg font-semibold text-gray-900">
              Validación de importación
            </h2>
            {fileName ? (
              <p className="text-sm text-gray-600 mt-1">
                Archivo: <span className="font-mono text-gray-800">{fileName}</span>
              </p>
            ) : null}
            <p className="text-sm text-gray-600 mt-2">
              Revise el resumen. Solo se añadirán las filas válidas. Las filas con error no forman parte del lote a
              importar.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 p-1 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100"
            aria-label="Cerrar"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 sm:p-5 space-y-4">
          <div className="flex flex-wrap gap-2 text-sm">
            <span className="inline-flex items-center rounded-full bg-emerald-50 px-2.5 py-0.5 font-medium text-emerald-800 ring-1 ring-inset ring-emerald-200">
              Filas listas: {nOk}
            </span>
            {nErr > 0 && (
              <span className="inline-flex items-center rounded-full bg-red-50 px-2.5 py-0.5 font-medium text-red-800 ring-1 ring-inset ring-red-200">
                Errores: {nErr}
              </span>
            )}
            {nWarn > 0 && (
              <span className="inline-flex items-center rounded-full bg-amber-50 px-2.5 py-0.5 font-medium text-amber-900 ring-1 ring-inset ring-amber-200">
                Advertencias: {nWarn}
              </span>
            )}
          </div>

          {nErr > 0 && (
            <div className="rounded-lg border border-red-200 bg-red-50/80 p-3 text-sm text-red-900">
              <p className="font-medium mb-1">No se pudo leer o rechazó (por fila):</p>
              <ul className="list-disc pl-5 space-y-0.5 max-h-32 overflow-y-auto">
                {errors.map((line, i) => (
                  <li key={i}>{line}</li>
                ))}
              </ul>
            </div>
          )}

          {nWarn > 0 && (
            <div className="rounded-lg border border-amber-200 bg-amber-50/80 p-3 text-sm text-amber-950">
              <p className="font-medium mb-1">Atención:</p>
              <ul className="list-disc pl-5 space-y-0.5 max-h-28 overflow-y-auto">
                {warnings.map((line, i) => (
                  <li key={i}>{line}</li>
                ))}
              </ul>
            </div>
          )}

          {nOk > 0 ? (
            <div>
              <p className="text-sm font-medium text-gray-800 mb-2">Vista previa de filas a añadir</p>
              <div className="border border-gray-200 rounded-lg overflow-x-auto max-h-64 overflow-y-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 text-left text-xs font-medium text-gray-600 uppercase">
                    <tr>
                      <th className="px-2 py-2 whitespace-nowrap">#</th>
                      <th className="px-2 py-2 min-w-[10rem]">Producto</th>
                      <th className="px-2 py-2 whitespace-nowrap">Evento kardex</th>
                      <th className="px-2 py-2 text-right">Cantidad</th>
                      <th className="px-2 py-2">Unidad</th>
                      <th className="px-2 py-2 min-w-[8rem]">Proyecto</th>
                      {!isExit && (
                        <>
                          <th className="px-2 py-2 text-right whitespace-nowrap">Costo u.</th>
                          <th className="px-2 py-2 text-right">Total</th>
                        </>
                      )}
                      {isExit && (
                        <th className="px-2 py-2 text-left text-gray-500 text-[11px] font-normal max-w-[10rem]">
                          Importes
                        </th>
                      )}
                      <th className="px-2 py-2 whitespace-nowrap">Condición</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {rows.map((row, i) => (
                      <tr key={i} className="hover:bg-gray-50/80">
                        <td className="px-2 py-1.5 text-gray-500">{i + 1}</td>
                        <td className="px-2 py-1.5">
                          <div className="font-medium text-gray-900 break-words">{row.description || '—'}</div>
                        </td>
                        <td className="px-2 py-1.5 whitespace-nowrap">{labelKardex(row.kardex_event)}</td>
                        <td className="px-2 py-1.5 text-right">
                          <div className="font-mono">{row.quantity != null ? row.quantity : '—'}</div>
                          <QuantityConversionHint
                            product={products.find((p) => String(p.id) === String(row.product_id))}
                            unitCode={row.unit_of_measure}
                            quantity={row.quantity}
                          />
                        </td>
                        <td className="px-2 py-1.5 min-w-[9rem]">
                          {canEditUnits ? (
                            <UnitSelect
                              value={row.unit_of_measure || ''}
                              onChange={(v) => handleUnitChange(i, v)}
                              units={units}
                              product={findProduct(row)}
                              compact
                            />
                          ) : (
                            <div>
                              <span className="whitespace-nowrap">{row.unit_of_measure || '—'}</span>
                              {row._unitFormula ? (
                                <p className="text-[10px] text-teal-700 mt-0.5">{row._unitFormula}</p>
                              ) : null}
                            </div>
                          )}
                        </td>
                        <td className="px-2 py-1.5 text-xs text-gray-700 break-words">
                          {projectLabel(row.project_id)}
                        </td>
                        {isExit ? (
                          <td className="px-2 py-1.5 text-xs text-gray-500 italic">Define el almacén</td>
                        ) : (
                          <>
                            <td className="px-2 py-1.5 text-right font-mono">
                              {row.unit_price != null ? row.unit_price : '0'}
                            </td>
                            <td className="px-2 py-1.5 text-right font-mono">
                              {row.total != null ? row.total : '0'}
                            </td>
                          </>
                        )}
                        <td className="px-2 py-1.5 whitespace-nowrap">{labelCondition(row.condition)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-600">No hay filas válidas para añadir. Corrija el archivo e intente otra vez.</p>
          )}
        </div>

        <div className="p-4 sm:p-5 border-t border-gray-200 flex flex-wrap justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={() => onConfirm(rows)}
            disabled={!canAdd}
            className="px-4 py-2 text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {canAdd ? `Añadir ${nOk} fila(s) a la guía` : 'No hay filas para añadir'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default GuideItemsImportPreviewModal;
